import React from 'react';

interface RGFallbackComponentProps {
  error: Error | null;
}

interface RGComponentErrorBoundaryProps<T> {
  children: React.ReactNode;
  fallback?: React.ComponentType<RGFallbackComponentProps & T>;
  fallbackProps?: T;
}

interface RGComponentErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class RGComponentErrorBoundary<T> extends React.Component<
  RGComponentErrorBoundaryProps<T>,
  RGComponentErrorBoundaryState
> {
  constructor(props: RGComponentErrorBoundaryProps<T>) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
    };
  }

  static getDerivedStateFromError(error: Error) {
    return {
      hasError: true,
      error: error,
    };
  }

  override render() {
    const { hasError, error } = this.state;
    const { children, fallback: FallbackComponent, fallbackProps } = this.props;

    if (hasError) {
      // reset the state so subsequent re-renders (with potentially fixed code)
      //  work properly
      this.resetState();

      if (FallbackComponent) {
        return <FallbackComponent error={error} {...(fallbackProps as T)} />;
      }
    }

    return children;
  }

  resetState() {
    // eslint-disable-next-line react/no-direct-mutation-state
    this.state = { hasError: false, error: null };
  }
}

export { RGComponentErrorBoundary };
